/**
 * Unified "Change Billing" form.
 *
 * Replaces the separate "Switch Payment Type" and "Change Frequency" dialogs
 * with a single form that changes payment type and/or billing frequency in one
 * atomic request. The monthly + invoice combination is never selectable: when
 * invoice is chosen the frequency control offers only quarterly and annual.
 */
import React, { useEffect, useRef, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Button,
  TextField,
} from '@mui/material'
import {
  BillingInfo,
  previewChangeBilling,
  changeBilling,
  ChangeBillingError,
  changeBillingErrorMessage,
  ChangeBillingPreviewResponse,
  ChangeBillingResponse,
  InvoiceStart,
} from '@/api/billingAPI'
import { billingEmailValidationError } from '@/components/InvoicePaymentForm'
import { TIER_PRICING, getBillingAmount, removeGst } from '@/utils/billingConstants'
import { toCanonicalFrequency, CanonicalFrequency } from '@/utils/frequency'

type PaymentType = 'stripe' | 'invoice'

interface ChangeBillingFormProps {
  open: boolean
  onClose: () => void
  orgUUID: string
  token: string
  billingEmail: string | null | undefined
  userEmail?: string
  onUpdateBillingInfo: (
    updates: Partial<BillingInfo>,
  ) => Promise<BillingInfo | null>
  currentPaymentType: string
  currentFrequency: string
  tier: number | null
  hasActiveDiscount?: boolean
  onComplete: (response: ChangeBillingResponse) => void
}

const STRIPE_FREQUENCIES: CanonicalFrequency[] = ['Monthly', 'Quarterly', 'Yearly']
const INVOICE_FREQUENCIES: CanonicalFrequency[] = ['Quarterly', 'Yearly']

const paymentLabel = (pt: PaymentType) =>
  pt === 'stripe' ? 'Credit card' : 'Invoice'

// Customer-facing label for a frequency selector option.
const frequencyOptionLabel = (f: CanonicalFrequency) =>
  f === 'Yearly' ? 'Annual (20% off)' : f

// Plain label used in the read-only "Currently:" header.
const plainFrequencyLabel = (f: CanonicalFrequency) =>
  f === 'Yearly' ? 'Annually' : f

const FREQUENCY_TO_BILLING: Record<CanonicalFrequency, 'monthly' | 'quarterly' | 'annual'> = {
  Monthly: 'monthly',
  Quarterly: 'quarterly',
  Yearly: 'annual',
}

const formatDate = (dateStr: string | null | undefined) => {
  if (!dateStr) return ''
  // Date-only ISO strings ("2026-06-15") are parsed by the JS Date
  // constructor as UTC midnight, then toLocaleDateString shifts them into
  // the browser's local timezone — so an Australian/NZ user with a positive
  // UTC offset sees the date roll forward one day, and US users see it
  // shift backward. Parse YYYY-MM-DD as a *local* date instead.
  const datePart = dateStr.split('T')[0]
  const ymd = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  const date = ymd
    ? new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]))
    : new Date(dateStr)
  return date.toLocaleDateString('en-NZ', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

export default function ChangeBillingForm({
  open,
  onClose,
  orgUUID,
  token,
  billingEmail,
  userEmail,
  onUpdateBillingInfo,
  currentPaymentType,
  currentFrequency,
  tier,
  hasActiveDiscount = false,
  onComplete,
}: ChangeBillingFormProps) {
  const currentPt: PaymentType =
    currentPaymentType?.toLowerCase() === 'invoice' ? 'invoice' : 'stripe'
  const currentFreq = toCanonicalFrequency(currentFrequency || 'Monthly')

  const [step, setStep] = useState<'select' | 'confirm'>('select')
  const [paymentType, setPaymentType] = useState<PaymentType>(currentPt)
  const [frequency, setFrequency] = useState<CanonicalFrequency>(currentFreq)

  const [preview, setPreview] = useState<ChangeBillingPreviewResponse | null>(null)
  const [invoiceStart, setInvoiceStart] = useState<InvoiceStart>('period_end')
  const [previewLoading, setPreviewLoading] = useState(false)
  const [previewError, setPreviewError] = useState<string | null>(null)

  const [submitLoading, setSubmitLoading] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [inlineBillingEmail, setInlineBillingEmail] = useState('')
  const [inlineBillingEmailError, setInlineBillingEmailError] = useState<
    string | null
  >(null)
  const [needsBillingEmailAtOpen, setNeedsBillingEmailAtOpen] = useState(false)
  const [lastSavedEmail, setLastSavedEmail] = useState<string | null>(null)
  const continueInFlightRef = useRef(false)
  const wasOpenRef = useRef(false)
  const attemptGenerationRef = useRef(0)

  // Reset to a clean step 1 each time the dialog opens, snapping away from the
  // monthly + invoice combination so a grandfathered customer cannot re-submit
  // it. currentPt/currentFreq are derived from props and must be in the
  // dependency array so the form re-syncs when the underlying billing state
  // changes (e.g. after a refetch while the dialog is open).
  useEffect(() => {
    if (!open) return
    setStep('select')
    setPaymentType(currentPt)
    setFrequency(currentPt === 'invoice' && currentFreq === 'Monthly' ? 'Quarterly' : currentFreq)
    setPreview(null)
    setPreviewError(null)
    setSubmitError(null)
    if (!continueInFlightRef.current) {
      setPreviewLoading(false)
    }
    setSubmitLoading(false)
  }, [open, currentPt, currentFreq])

  // Capture blankness and signup-style prefill once per dialog attempt. Parent
  // billing-info updates while the dialog is open must not hide the field.
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      wasOpenRef.current = true
      attemptGenerationRef.current += 1
      const needsEmail = !billingEmail?.trim()
      setNeedsBillingEmailAtOpen(needsEmail)
      setInlineBillingEmail(
        (billingEmail?.trim() ? billingEmail : userEmail) || '',
      )
      setInlineBillingEmailError(null)
      setLastSavedEmail(needsEmail ? null : billingEmail || null)
    } else if (!open && wasOpenRef.current) {
      wasOpenRef.current = false
      attemptGenerationRef.current += 1
    }
  }, [open, currentPt, currentFreq, billingEmail, userEmail])

  // Invoice billing is never monthly — keep the selection valid if the user
  // switches to invoice while monthly is selected.
  useEffect(() => {
    if (paymentType === 'invoice' && frequency === 'Monthly') {
      setFrequency('Quarterly')
    }
  }, [paymentType, frequency])

  const availableFrequencies =
    paymentType === 'invoice' ? INVOICE_FREQUENCIES : STRIPE_FREQUENCIES

  const isNoop = paymentType === currentPt && frequency === currentFreq
  const showInlineBillingEmail =
    paymentType === 'invoice' && needsBillingEmailAtOpen

  const handleClose = () => {
    if (continueInFlightRef.current || previewLoading || submitLoading) return
    onClose()
  }

  const handleContinue = async () => {
    if (continueInFlightRef.current || previewLoading || isNoop) return
    continueInFlightRef.current = true
    const attemptGeneration = attemptGenerationRef.current
    setPreviewLoading(true)
    setPreviewError(null)
    try {
      if (showInlineBillingEmail) {
        const validationError =
          billingEmailValidationError(inlineBillingEmail)
        if (validationError) {
          setInlineBillingEmailError(validationError)
          return
        }
        setInlineBillingEmailError(null)

        if (inlineBillingEmail !== lastSavedEmail) {
          let updatedInfo: BillingInfo | null
          try {
            updatedInfo = await onUpdateBillingInfo({
              billing_email: inlineBillingEmail,
            })
          } catch {
            updatedInfo = null
          }
          if (attemptGeneration !== attemptGenerationRef.current) return
          if (!updatedInfo) {
            setPreviewError(
              'Failed to save billing email. Please try again.',
            )
            return
          }
          setLastSavedEmail(inlineBillingEmail)
        }
      }

      const result = await previewChangeBilling(orgUUID, token, paymentType, frequency)
      if (attemptGeneration !== attemptGenerationRef.current) return
      setPreview(result)
      setInvoiceStart(result.default_invoice_start ?? 'period_end')
      setStep('confirm')
    } catch (err) {
      if (attemptGeneration !== attemptGenerationRef.current) return
      setPreviewError(
        err instanceof ChangeBillingError
          ? changeBillingErrorMessage(err)
          : 'Something went wrong. Please try again.',
      )
    } finally {
      continueInFlightRef.current = false
      setPreviewLoading(false)
    }
  }

  const handleConfirm = async () => {
    if (submitLoading) return
    setSubmitLoading(true)
    setSubmitError(null)
    try {
      const choosesStart =
        preview?.route === 'switch_to_invoice' && !preview.already_pending
      const response = choosesStart
        ? await changeBilling(orgUUID, token, paymentType, frequency, invoiceStart)
        : await changeBilling(orgUUID, token, paymentType, frequency)
      onComplete(response)
    } catch (err) {
      setSubmitError(
        err instanceof ChangeBillingError
          ? changeBillingErrorMessage(err)
          : 'Something went wrong. Please try again.',
      )
    } finally {
      setSubmitLoading(false)
    }
  }

  const handleBack = () => {
    setStep('select')
    setPreview(null)
    setSubmitError(null)
  }

  const tierPricing = tier !== null && tier !== undefined ? TIER_PRICING[tier] : null

  const frequencyPriceHint = (f: CanonicalFrequency): string | null => {
    if (!tierPricing) return null
    const amount = getBillingAmount(tierPricing.monthly, FREQUENCY_TO_BILLING[f])
    const period = f === 'Monthly' ? '/month' : f === 'Quarterly' ? '/quarter' : '/year'
    return `$${amount.toFixed(2)}${period} +GST`
  }

  const renderPreviewSummary = (p: ChangeBillingPreviewResponse) => {
    const targetFreqLabel = frequencyOptionLabel(frequency)

    if (p.already_pending) {
      return (
        <Alert severity="info" sx={{ mb: 2 }}>
          A switch to invoice billing is already scheduled
          {p.effective_date ? ` for ${formatDate(p.effective_date)}` : ''}.
          Confirming again will not change anything.
        </Alert>
      )
    }

    switch (p.route) {
      case 'noop':
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            This is already your billing setup — nothing to change.
          </Alert>
        )
      case 'frequency_change':
        if (p.proration_preview && p.proration_preview.amount_due != null) {
          const amount = (p.proration_preview.amount_due ?? 0) / 100
          const currency = (p.proration_preview.currency ?? 'nzd').toUpperCase()
          return (
            <Alert severity="info" sx={{ mb: 2 }}>
              Switching to <strong>{targetFreqLabel}</strong> billing. You&apos;ll be
              charged approximately{' '}
              <strong>
                ${amount.toFixed(2)} {currency}
              </strong>
              {p.proration_preview.due_date
                ? ` on ${formatDate(p.proration_preview.due_date)}`
                : ' at your next billing date'}
              . This is an estimate only.
            </Alert>
          )
        }
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            Switching to <strong>{targetFreqLabel}</strong> billing from your next
            billing cycle
            {p.new_billing_amount != null
              ? ` — $${removeGst(p.new_billing_amount).toFixed(2)} +GST per period`
              : ''}
            .
          </Alert>
        )
      case 'switch_to_stripe':
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            <strong>Switching to credit-card billing ({targetFreqLabel}).</strong>{' '}
            After you confirm, complete Stripe checkout to enter your card.
            {p.invoice_status === 'unpaid' && p.outstanding_amount ? (
              <Box sx={{ mt: 1 }}>
                An outstanding invoice of{' '}
                <strong>${removeGst(p.outstanding_amount).toFixed(2)} +GST</strong>{' '}
                will be collected with your first card payment.
              </Box>
            ) : null}
            {p.invoice_status === 'paid' && p.billing_starts ? (
              <Box sx={{ mt: 1 }}>
                Your invoice is paid through{' '}
                <strong>{formatDate(p.billing_starts)}</strong>; card billing
                begins then.
              </Box>
            ) : null}
          </Alert>
        )
      case 'switch_to_invoice': {
        const dates = p.first_invoice_dates
        const periodEnd = dates?.period_end ?? null
        const firstInvoiceDate =
          invoiceStart === 'period_end' && periodEnd ? periodEnd : dates?.now
        return (
          <Box sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <strong>Switching to invoice billing ({targetFreqLabel}).</strong>
            </Alert>
            {periodEnd ? (
              <FormControl sx={{ mb: 2 }}>
                <FormLabel id="change-billing-invoice-start-label">
                  When should invoice billing start?
                </FormLabel>
                <RadioGroup
                  aria-labelledby="change-billing-invoice-start-label"
                  data-testid="change-billing-invoice-start"
                  value={invoiceStart}
                  onChange={(e) => setInvoiceStart(e.target.value as InvoiceStart)}
                >
                  <FormControlLabel
                    value="period_end"
                    control={<Radio />}
                    label={`When the paid card period ends (${formatDate(periodEnd)})`}
                  />
                  <FormControlLabel
                    value="now"
                    control={<Radio />}
                    label="Now — cancel the card subscription today, with no refund"
                  />
                </RadioGroup>
              </FormControl>
            ) : (
              <Typography variant="body2" sx={{ mb: 2 }}>
                There is no paid card period left, so invoice billing starts now.
              </Typography>
            )}
            {firstInvoiceDate ? (
              <Alert severity="warning" data-testid="change-billing-first-invoice">
                The first invoice will be issued on{' '}
                <strong>{formatDate(firstInvoiceDate)}</strong>.
              </Alert>
            ) : null}
          </Box>
        )
      }
      case 'revert_pending':
        return (
          <Alert severity="info" sx={{ mb: 2 }}>
            This cancels your scheduled switch to invoice billing — you&apos;ll
            stay on credit card.
          </Alert>
        )
      default:
        return null
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      data-testid="change-billing-form"
    >
      <DialogTitle>Change Billing</DialogTitle>

      {step === 'select' ? (
        <>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Currently: <strong>{paymentLabel(currentPt)}</strong>,{' '}
              <strong>{plainFrequencyLabel(currentFreq)}</strong>
            </Typography>

            {previewError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {previewError}
              </Alert>
            )}

            {hasActiveDiscount && paymentType === 'invoice' && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Your active percentage discount will be included in invoice
                billing previews and external invoice updates.
              </Alert>
            )}

            <FormControl sx={{ mb: 2 }}>
              <FormLabel id="change-billing-payment-type-label">
                Payment method
              </FormLabel>
              <RadioGroup
                aria-labelledby="change-billing-payment-type-label"
                data-testid="change-billing-payment-type"
                value={paymentType}
                onChange={(e) => setPaymentType(e.target.value as PaymentType)}
              >
                <FormControlLabel
                  value="stripe"
                  control={<Radio />}
                  label="Credit card (Stripe)"
                />
                <FormControlLabel
                  value="invoice"
                  control={<Radio />}
                  label="Invoice"
                />
              </RadioGroup>
            </FormControl>

            <FormControl>
              <FormLabel id="change-billing-frequency-label">
                Billing frequency
              </FormLabel>
              <RadioGroup
                aria-labelledby="change-billing-frequency-label"
                data-testid="change-billing-frequency"
                value={frequency}
                onChange={(e) =>
                  setFrequency(e.target.value as CanonicalFrequency)
                }
              >
                {availableFrequencies.map((f) => {
                  const hint = frequencyPriceHint(f)
                  return (
                    <FormControlLabel
                      key={f}
                      value={f}
                      control={<Radio />}
                      label={
                        hint
                          ? `${frequencyOptionLabel(f)} — ${hint}`
                          : frequencyOptionLabel(f)
                      }
                    />
                  )
                })}
              </RadioGroup>
              {paymentType === 'invoice' && (
                <Typography variant="caption" color="text.secondary">
                  Invoice billing is quarterly or annual — monthly is credit
                  card only.
                </Typography>
              )}
            </FormControl>

            {showInlineBillingEmail && (
              <TextField
                fullWidth
                required
                type="email"
                label="Billing Email"
                value={inlineBillingEmail}
                onChange={(event) => {
                  setInlineBillingEmail(event.target.value)
                  if (inlineBillingEmailError) {
                    setInlineBillingEmailError(null)
                  }
                }}
                error={Boolean(inlineBillingEmailError)}
                helperText={
                  inlineBillingEmailError ||
                  'Invoices will be sent to this email'
                }
                disabled={previewLoading}
                sx={{ mt: 2 }}
              />
            )}

            {isNoop && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This is your current billing setup. Pick a different payment
                method or frequency to make a change.
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} disabled={previewLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              data-testid="change-billing-continue"
              onClick={handleContinue}
              disabled={previewLoading || isNoop}
              sx={{ bgcolor: '#1C3045', '&:hover': { bgcolor: '#2a4460' } }}
            >
              {previewLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Continue'
              )}
            </Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogContent>
            {preview && renderPreviewSummary(preview)}

            {submitError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {submitError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              data-testid="change-billing-back"
              onClick={handleBack}
              disabled={submitLoading}
            >
              Back
            </Button>
            <Button
              variant="contained"
              data-testid="change-billing-confirm"
              onClick={handleConfirm}
              disabled={submitLoading}
              sx={{ bgcolor: '#1C3045', '&:hover': { bgcolor: '#2a4460' } }}
            >
              {submitLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Confirm'
              )}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}
